import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import countriesAndTimezones from 'countries-and-timezones';
import { UsaStates } from 'usa-states';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import { Field, reduxForm, SubmissionError, getFormValues, FieldArray } from 'redux-form';

import TextField from 'components/fields/TextField';
import FormError from 'components/common/FormError';
import CheckboxField from 'components/fields/CheckboxField';
import AutosuggestField from 'components/fields/AutosuggestField';

import {
  createInsurance,
  updateInsurance,
} from 'redux/modules/insurances';

import {
  requestFetchClaimFillingCodes,
  requestSearchClaimFillingCodes,
} from 'redux/modules/claimFillingCodes';

import {
  requestFetchChangeInfo,
  requestSearchChangeInfo,
} from 'redux/modules/changeInfo';

import {
  requestFetchClaimMDInfo,
  requestSearchClaimMDInfo,
} from 'redux/modules/claimMDInfo';

import { accountLoginFail } from 'redux/modules/account';
import { requestFetchPlacesAutocomplete } from 'redux/modules/placesAutocomplete';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

import objectDiff from 'utils/objectDiff';

import transmissionMethods from 'config/transmissionMethods';
import insuranceOrderOfBenefits from 'config/insuranceOrderOfBenefits';

import PayerCreateEditServicesField from './PayerCreateEditServicesField';
import PayerContactFieldArray from './PayerContactFieldArray';

const insuranceProfitPercentTypes = [
  'Default',
  'Medicare',
  'Medicaid',
];

const usaStates = new UsaStates();
const usaTimeZones = countriesAndTimezones.getTimezonesForCountry('US')
  .map(item => item.name);

const styles = () => ({
  grid: {
    marginTop: 10,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues);

    if (normalizedValues['Patient Map Payer Name'] !== undefined
      && normalizedValues['Patient Map Payer Name'] === '') {
      normalizedValues['Patient Map Payer Name'] = null;
    }

    if (normalizedValues['Default Order Benefits'] !== undefined
      && normalizedValues['Default Order Benefits'] === '') {
      normalizedValues['Default Order Benefits'] = null;
    }

    if (normalizedValues['Default Transmission Method'] !== undefined
      && normalizedValues['Default Transmission Method'] === '') {
      normalizedValues['Default Transmission Method'] = null;
    }
    if (normalizedValues['Profit Percent Type'] === 'Medicare') {
      normalizedValues['Profit Percent Medicare'] = Number(normalizedValues['Profit Percent']);
      normalizedValues['Profit Percent Medicaid'] = null;
    }
    if (normalizedValues['Profit Percent Type'] === 'Medicaid') {
      normalizedValues['Profit Percent Medicaid'] = Number(normalizedValues['Profit Percent']);
      normalizedValues['Profit Percent Medicare'] = null;
    }
    if (normalizedValues['Profit Percent Type'] === 'Default') {

      normalizedValues['Profit Percent Medicaid'] = null;
      normalizedValues['Profit Percent Medicare'] = null;
    }

    if (normalizedValues.Street === '') {
      normalizedValues.Street = null;
    }

    if (normalizedValues.Suite === '') {
      normalizedValues.Suite = null;
    }

    if (normalizedValues.City === '') {
      normalizedValues.City = null;
    }

    if (normalizedValues.State === '') {
      normalizedValues.State = null;
    }

    if (normalizedValues['Zip Code'] === '') {
      normalizedValues['Zip Code'] = null;
    }
    
    if (normalizedValues.Fax === '') {
      normalizedValues.Fax = null;
    }

    if (normalizedValues['Contacts Phone']
      || normalizedValues['Contacts Fax']
      || normalizedValues['Contacts Email']) {
      normalizedValues.Contacts = [
        ...normalizedValues['Contacts Phone'].filter(item => item.Value).map(item => ({
          Value: item.Value,
          ...item.Description ? { Description: item.Description } : {},
          Type: 'Phone',
        })),
        ...normalizedValues['Contacts Fax'].filter(item => item.Value).map(item => ({
          Value: item.Value,
          ...item.Description ? { Description: item.Description } : {},
          Type: 'Fax',
        })),
        ...normalizedValues['Contacts Email'].filter(item => item.Value).map(item => ({
          Value: item.Value,
          ...item.Description ? { Description: item.Description } : {},
          Type: 'Email',
        })),
      ];
    }

    delete normalizedValues['Profit Percent Type'];
    delete normalizedValues['Profit Percent'];
    delete normalizedValues['Default Claim Filling Code Name'];
    delete normalizedValues['Selected Default Claim Filling Code Name'];
    delete normalizedValues['Change Info Trading Partner Name'];
    delete normalizedValues['Selected Change Info Trading Partner Name'];
    delete normalizedValues['Claim MD Payer Name'];
    delete normalizedValues['Selected Claim MD Payer Name'];
    delete normalizedValues['Payer ID'];
    delete normalizedValues['Selected Payer ID'];
    delete normalizedValues.Services;
    delete normalizedValues['Contacts Phone'];
    delete normalizedValues['Contacts Fax'];
    delete normalizedValues['Contacts Email'];

    return normalizedValues;
  };

  if (props.ID) {
    if (props.pristine) {
      throw new SubmissionError({
        _error: 'Nothing to submit',
      });
    }
    const diffValues = objectDiff(values, props.initialValues);
    return new Promise((resolve, reject) => {
      dispatch(updateInsurance({
        values: {
          ID: props.ID,
          ...normalizeValues({
            ...diffValues,
            ...diffValues['Profit Percent Type'] || diffValues['Profit Percent'] ? {
              'Profit Percent Type': values['Profit Percent Type'],
              'Profit Percent': values['Profit Percent'] ? values['Profit Percent'].replace('%', '') : values['Profit Percent'],
            } : {},
            ...diffValues['Contacts Phone'] || diffValues['Contacts Fax'] || diffValues['Contacts Email'] ? {
              'Contacts Phone': values['Contacts Phone'],
              'Contacts Fax': values['Contacts Fax'],
              'Contacts Email': values['Contacts Email'],
            } : {},
          }),
        },
        resolve,
        reject,
      }));
    }).then(res => res,
      (err) => {
        if (err.error) {
          throw new SubmissionError({ _error: err.error });
        }
        throw new SubmissionError(err);
      });
  }
  return new Promise((resolve, reject) => {
    dispatch(createInsurance({
      values: normalizeValues(values),
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.error });
      }
      throw new SubmissionError(err);
    });
}

function onChange(values, dispatch, props, prevValues) {
  if (values['Default Claim Filling Code ID']
    && values['Selected Default Claim Filling Code Name'] !== values['Default Claim Filling Code Name']) {
    dispatch(props.change('Default Claim Filling Code ID', null));
  }

  if (values['Change Info ID']) {
    if (values['Selected Change Info Trading Partner Name'] !== values['Change Info Trading Partner Name']) {
      dispatch(props.change('Change Info ID', null));
      dispatch(props.change('Selected Change Info Trading Partner Name', ''));
      dispatch(props.change('Selected CPID', ''));
      dispatch(props.change('CPID', ''));
      dispatch(props.change('Services', null));
    }
    if (values['Selected CPID'] !== values.CPID) {
      dispatch(props.change('Change Info ID', null));
      dispatch(props.change('Selected CPID', ''));
      dispatch(props.change('Selected Change Info Trading Partner Name', ''));
      dispatch(props.change('Change Info Trading Partner Name', ''));
      dispatch(props.change('Services', null));
    }
  }
  if (values['Claim MD ID']) {
    if (values['Selected Claim MD Payer Name'] !== values['Claim MD Payer Name']) {
      dispatch(props.change('Claim MD ID', null));
      dispatch(props.change('Selected Claim MD Payer Name', ''));
      dispatch(props.change('Selected Payer ID', ''));
      dispatch(props.change('Payer ID', ''));
      dispatch(props.change('Services', null));
    }
    if (values['Selected Payer ID'] !== values['Payer ID']) {
      dispatch(props.change('Claim MD ID', null));
      dispatch(props.change('Selected Payer ID', ''));
      dispatch(props.change('Selected Claim MD Payer Name', ''));
      dispatch(props.change('Claim MD Payer Name', ''));
      dispatch(props.change('Services', null));
    }
  }
  if (values['Profit Percent']
    && values['Profit Percent Type'] === 'Default' && prevValues['Profit Percent Type'] !== 'Default') {
    dispatch(props.change('Profit Percent', null));
  }
}

const validateForm = (values) => {
  const errors = {};

  if (values['Default Claim Filling Code Name']
    && values['Selected Default Claim Filling Code Name'] !== values['Default Claim Filling Code Name']) {
    errors['Default Claim Filling Code Name'] = 'Please, select Claim Filling Code from list';
  }

  if (values['Change Info Trading Partner Name']
    && values['Selected Change Info Trading Partner Name'] !== values['Change Info Trading Partner Name']) {
    errors['Change Info Trading Partner Name'] = 'Please, select Trading Partner Name from list';
  }

  if (values['Claim MD Payer Name']
    && values['Selected Claim MD Payer Name'] !== values['Claim MD Payer Name']) {
    errors['Claim MD Payer Name'] = 'Please, select Claim MD Payer Name from list';
  }

  if (values['Time Zone'] && usaTimeZones.indexOf(values['Time Zone']) === -1) {
    errors['Time Zone'] = 'Please, select Time Zone from list';
  }

  if (values['Contacts Phone'] && values['Contacts Phone'].length > 0){
    errors['Contacts Phone'] = values['Contacts Phone'].map(item => {
      if (!item.Value && item.Description) {
        return {
          Value: 'Please, enter value for description',
        };
      }
      return undefined;
    });
    if (errors['Contacts Phone'].filter(item => item !== undefined).length === 0) {
      delete errors['Contacts Phone'];
    }
  }

  if (values['Contacts Fax'] && values['Contacts Fax'].length > 0){
    errors['Contacts Fax'] = values['Contacts Fax'].map(item => {
      if (!item.Value && item.Description) {
        return {
          Value: 'Please, enter value for description',
        };
      }
      return undefined;
    });
    if (errors['Contacts Fax'].filter(item => item !== undefined).length === 0) {
      delete errors['Contacts Fax'];
    }
  }

  if (values['Contacts Email'] && values['Contacts Email'].length > 0){
    errors['Contacts Email'] = values['Contacts Email'].map(item => {
      if (!item.Value && item.Description) {
        return {
          Value: 'Please, enter value for description',
        };
      }
      return undefined;
    });
    if (errors['Contacts Email'].filter(item => item !== undefined).length === 0) {
      delete errors['Contacts Email'];
    }
  }

  return errors;
};

const PayerCreateEditForm = ({
  handleSubmit,
  className,
  error,
  change,
  actions,
  formValues,
  classes,
}) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
    className={className}
  >
    {error &&
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <FormError>
            {error}
          </FormError>
        </Grid>
      </Grid>
    }
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Payer Name"
          name="Payer Name"
          label="Payer Name"
          component={TextField}
          validate={[validate.required]}
          errorPlacement="bottom"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
      alignItems="flex-end"
    >
      <Grid
        item
        xs={6}
        md={5}
      >
        <Field
          id="Default Order Benefits"
          name="Default Order Benefits"
          label="Order of Benefits"
          component={AutosuggestField}
          fullWidth
          errorPlacement="top"
          renderSuggestionContent={suggestion => suggestion}
          onSuggestionSelected={suggestion => suggestion}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(insuranceOrderOfBenefits
                  .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
              } else {
                resolve(insuranceOrderOfBenefits);
              }
            } else {
              resolve([]);
            }
          }))}
        />
      </Grid>
      <Grid
        item
        xs={6}
        md={4}
      >
        <Field
          id="Default Transmission Method"
          name="Default Transmission Method"
          label="Transmission Method"
          component={AutosuggestField}
          placeholder="-Select Transmission Method-"
          errorPlacement="top"
          fullWidth
          renderSuggestionContent={suggestion => suggestion}
          onSuggestionSelected={suggestion => suggestion}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(transmissionMethods
                  .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
              } else {
                resolve(transmissionMethods);
              }
            } else {
              resolve([]);
            }
          }))}
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
      alignItems="flex-end"
    >
      <Grid
        item
        xs={5}
      >
        <Field
          id="Profit Percent Type"
          name="Profit Percent Type"
          label="Profit Percent Type"
          component={AutosuggestField}
          fullWidth
          errorPlacement="top"
          suggestionsPlacement="top"
          renderSuggestionContent={suggestion => suggestion}
          onSuggestionSelected={suggestion => suggestion}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(insuranceProfitPercentTypes
                  .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
              } else {
                resolve(insuranceProfitPercentTypes);
              }
            } else {
              resolve([]);
            }
          }))}
        />
      </Grid>
      <Grid
        item
        xs={4}
      >
        <Field
          id="Profit Percent"
          name="Profit Percent"
          label="Profit Percent"
          component={TextField}
          normalize={normalize.profitPercent}
          errorPlacement="bottom"
          disabled={!formValues['Profit Percent Type'] || formValues['Profit Percent Type'] === 'Default'}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={3}
      >
        <Field
          id="Is Active"
          name="Is Active"
          label="Active"
          component={CheckboxField}
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Default Claim Filling Code Name"
          name="Default Claim Filling Code Name"
          label="Payer Type"
          component={AutosuggestField}
          errorPlacement="top"
          fullWidth
          renderSuggestionContent={suggestion => suggestion['Full Name']}
          onSuggestionSelected={(suggestion) => {
            change('Default Claim Filling Code ID', suggestion.ID);
            change('Selected Default Claim Filling Code Name', suggestion['Full Name']);
            return suggestion['Full Name'];
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchClaimFillingCodes({
                filters: [
                  {
                    name: 'Full Name',
                    operation: 'contains',
                    val: value,
                    case_sensitivity: false,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchClaimFillingCodes({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
        />
      </Grid>
    </Grid>
    
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={8}
      >
        <Field
          id="Change Info Trading Partner Name"
          name="Change Info Trading Partner Name"
          label="Change Trading Partner Name"
          component={AutosuggestField}
          errorPlacement="top"
          fullWidth
          suggestionsPlacement="top"
          disabled
          renderSuggestionContent={suggestion => suggestion['Trading Partner Name']}
          onSuggestionSelected={(suggestion) => {   
            change('Selected Change Info Trading Partner Name', suggestion['Trading Partner Name']);
            change('Selected CPID', suggestion['Trading Partner Code']);
            change('CPID', suggestion['Trading Partner Code']);
            change('Change Info ID', suggestion.ID);
            return suggestion['Trading Partner Name'];
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchChangeInfo({
                filters: [
                  {
                    name: 'Trading Partner Name',
                    operation: 'contains',
                    val: value,
                    case_sensitivity: false,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchChangeInfo({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
        />
      </Grid>
      <Grid
        item
        xs={4}
      >
        <Field
          id="CPID"
          name="CPID"
          label="CPID"
          component={AutosuggestField}
          errorPlacement="top"
          fullWidth
          listWidth="auto-right"
          suggestionsPlacement="top"
          disabled
          renderSuggestionContent={suggestion => suggestion['Trading Partner Name']}
          onSuggestionSelected={(suggestion) => {
            change('Selected Change Info Trading Partner Name', suggestion['Trading Partner Name']);
            change('Change Info Trading Partner Name', suggestion['Trading Partner Name']);
            change('Selected CPID', suggestion['Trading Partner Code']);
            change('Change Info ID', suggestion.ID);
            return suggestion['Trading Partner Code'];
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchChangeInfo({
                filters: [
                  {
                    name: 'Trading Partner Code',
                    operation: 'contains',
                    val: value,
                    case_sensitivity: false,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchChangeInfo({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={8}
      >
        <Field
          id="Claim MD Payer Name"
          name="Claim MD Payer Name"
          label="Claim MD Payer Name"
          component={AutosuggestField}
          errorPlacement="top"
          fullWidth
          suggestionsPlacement="top"
          renderSuggestionContent={suggestion => suggestion['Payer Name']}
          onSuggestionSelected={(suggestion) => {
            const services = [
              ...suggestion['Claims 1500'] ? ['Claims'] : [],
              ...suggestion['Eligibility'] ? ['Eligibility'] : [],
              ...suggestion.Era ? ['Remittance'] : [],
              ...suggestion['Attachment'] ? ['Attachment'] : [],
              ...suggestion['Secondary Support'] ? ['Secondary Support'] : [],
            ];
            change('Selected Claim MD Payer Name', suggestion['Payer Name']);
            change('Selected Payer ID', suggestion['Payer ID']);
            change('Payer ID', suggestion['Payer ID']);
            change('Services', services);
            change('Claim MD ID', suggestion.ID);
            return suggestion['Payer Name'];
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchClaimMDInfo({
                filters: [
                  {
                    name: 'Payer Name',
                    operation: 'contains',
                    val: value,
                    case_sensitivity: false,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchClaimMDInfo({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
        />
      </Grid>
      <Grid
        item
        xs={4}
      >
        <Field
          id="Payer ID"
          name="Payer ID"
          label="Payer ID"
          component={AutosuggestField}
          errorPlacement="top"
          fullWidth
          listWidth="auto-right"
          suggestionsPlacement="top"
          renderSuggestionContent={suggestion => suggestion['Payer Name']}
          onSuggestionSelected={(suggestion) => {
            const services = [
              ...suggestion['Claims 1500'] ? ['Claims'] : [],
              ...suggestion['Eligibility'] ? ['Eligibility'] : [],
              ...suggestion.Era ? ['Remittance'] : [],
              ...suggestion['Attachment'] ? ['Attachment'] : [],
              ...suggestion['Secondary Support'] ? ['Secondary Support'] : [],
            ];
            change('Selected Claim MD Payer Name', suggestion['Payer Name']);
            change('Claim MD Payer Name', suggestion['Payer Name']);
            change('Selected Payer ID', suggestion['Payer ID']);
            change('Services', services);
            change('Claim MD ID', suggestion.ID);
            return suggestion['Payer ID'];
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchClaimMDInfo({
                filters: [
                  {
                    name: 'Payer ID',
                    operation: 'contains',
                    val: value,
                    case_sensitivity: false,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchClaimMDInfo({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid item xs={9} md={8}>
        <Field
          id="Street"
          name="Street"
          label="Street"
          component={AutosuggestField}
          placeholder="Lookup Address"
          fullWidth
          renderSuggestionContent={(suggestion) => suggestion.address.label}
          onSuggestionSelected={(suggestion) => {
            if (suggestion.address.city) {
              change('City', suggestion.address.city);
            }
            if (suggestion.address.postalCode) {
              change('Zip Code', suggestion.address.postalCode);
            }
            if (suggestion.address.stateCode) {
              change('State', suggestion.address.stateCode);
            }
            if (suggestion.address.street) {
              if (suggestion.address.houseNumber) {
                return `${suggestion.address.houseNumber} ${suggestion.address.street}`;
              }
              return suggestion.address.street;
            }
            return suggestion.address.label;
          }}
          fetchSuggestions={({ value }) => (
            value.length > 0
              ? requestFetchPlacesAutocomplete({
                q: value,
                in: 'countryCode:USA',
                limit: 5,
              }).then(res => res.items,
                err => err)
              : new Promise(resolve => resolve([]))
          )}
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          id="Suite"
          name="Suite"
          label="Suite"
          component={TextField}
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid item xs={6} md={5}>
        <Field
          id="City"
          name="City"
          label="City"
          component={TextField}
          fullWidth
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          id="State"
          name="State"
          label="State"
          component={AutosuggestField}
          normalize={normalize.uppercase}
          fullWidth
          renderSuggestionContent={suggestion => suggestion.abbreviation}
          onSuggestionSelected={suggestion => suggestion.abbreviation}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(usaStates.states.filter(item =>
                  item.abbreviation.indexOf(value) === 0));
              } else {
                resolve(usaStates.states);
              }
            } else {
              resolve([]);
            }
          }))}
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          id="Zip Code"
          name="Zip Code"
          label="Zip Code"
          component={TextField}
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <FieldArray
          name="Contacts Phone"
          component={PayerContactFieldArray}
          variant="phone"
        />
      </Grid>
    </Grid>
    <Grid
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <FieldArray
          name="Contacts Fax"
          component={PayerContactFieldArray}
          variant="fax"
        />
      </Grid>
    </Grid>
    <Grid
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <FieldArray
          name="Contacts Email"
          component={PayerContactFieldArray}
          variant="email"
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid item xs={8}>
        <Field
          id="Services"
          name="Services"
          label="Services"
          component={PayerCreateEditServicesField}
          fullWidth
        />
      </Grid>
    </Grid>
  </form>
);

PayerCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  error: PropTypes.string,
  change: PropTypes.func.isRequired,
  className: PropTypes.string,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
  }).isRequired,
  formValues: PropTypes.shape({
    'Profit Percent Type': PropTypes.string,
  }),
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
};

PayerCreateEditForm.defaultProps = {
  className: '',
  error: null,
  formValues: {},
};

const mapStateToProps = (store, ownProps) => {
  const formValues = getFormValues(ownProps.form)(store);
  return {
    formValues,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(reduxForm({
  form: 'payerCreate',
  onSubmit,
  onChange,
  validate: validateForm,
  initialValues: {
    'Profit Percent Type': 'Default',
    'Contacts Phone': [
      {
        Value: '',
        Description: '',
      }
    ],
    'Contacts Fax': [
      {
        Value: '',
        Description: '',
      }
    ],
    'Contacts Email': [
      {
        Value: '',
        Description: '',
      }
    ],
  },
})(withStyles(styles)(PayerCreateEditForm)));



// WEBPACK FOOTER //
// ./src/components/settings/PayerCreateEditDialog/PayerCreateEditForm.js